import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useSelectionState } from '../../../../../../hooks/useSelectionState';
import { AiButton } from '../../../../../../web/components-v2/AiButton';
import { Button } from '../../../../../../web/components-v2/Button';
import { usePracticeCallAnalysis } from '../../hooks/usePracticeCallAnalysis';
import { DefaultPracticeCallFilter, usePracticeCallsSearch } from '../../hooks/usePracticeCallsSearch';
import { styleSheet } from '../../styles';
import { ANALYSIS_TYPES } from '../../types';
import { CallAnalysisModal } from '../CallAnalysisModal';
import { CustomSystemMessageModal } from '../CustomSystemMessageModal';
import { PracticeCallFilters } from '../PracticeCallFilters';
import { PracticeCallsTable } from '../PracticeCallsTable';

// Practice calls don't need onSDRClick since there's no SDR column in the practice calls table
export const PracticeCallsTab: React.FC = () => {
	const [customSystemMessage, setCustomSystemMessage] = React.useState<string>('');
	const [isMessageModalOpen, setIsMessageModalOpen] = React.useState<boolean>(false);

	const {
		filter,
		setFilter,
		calls,
		isLoading: isLoadingCalls,
		loadMore,
		hasNextPage,
		isFetchingNextPage,
	} = usePracticeCallsSearch();

	// Sorting state (local since PhoneCall endpoint may not support sorting the same way)
	const [sort, setSort] = React.useState<Api.ISortDescriptor>({
		sort: 'desc',
		sortBy: 'creationDate',
	});

	// Get selectable call IDs (only calls with transcriptions)
	const selectableCallIds = React.useMemo(
		() => calls.filter(call => call.hasTranscription).map(call => call.phoneCall.id),
		[calls]
	);

	// Use the selection state hook for shift-click functionality
	const {
		selectedRows: selectedCallIds,
		excludedRows,
		toggleRow: handleToggleCall,
		toggleSelectAll: handleToggleSelectAll,
		reset: resetSelection,
		isSelected: isCallSelected,
		isAllSelected,
		isPartialSelected,
	} = useSelectionState({ rows: selectableCallIds });

	// Force re-render after bulk selection
	const [bulkSelectionKey, setBulkSelectionKey] = React.useState(0);

	// Multi-call analysis - use practice call specific hook
	const selectedCalls = React.useMemo(() => {
		const callsWithTranscription = calls.filter(call => call.hasTranscription);
		if (isAllSelected) {
			return callsWithTranscription.filter(call => !excludedRows.includes(call.phoneCall.id));
		}
		return callsWithTranscription.filter(call => selectedCallIds.includes(call.phoneCall.id));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [calls, selectedCallIds, isAllSelected, excludedRows, bulkSelectionKey]);

	const {
		selectedAnalysisType: multiSelectedAnalysisType,
		isAnalysisModalOpen: isMultiAnalysisModalOpen,
		startMultiAnalysis,
		closeAnalysis: closeMultiAnalysis,
		aiResponse: multiAiResponse,
		isLoading: isLoadingMultiAnalysis,
		conversationMessages: multiConversationMessages,
		conversationId: multiConversationId,
		sendFollowUp: sendMultiFollowUp,
		lastAiResponseCompleted: lastMultiAiResponseCompleted,
		selectedCallCount,
	} = usePracticeCallAnalysis({
		selectedCalls,
		companyId: selectedCalls[0]?.company?.id,
		source: 'practice_call_analysis',
	});

	const handleCloseMultiAnalysis = React.useCallback(() => {
		closeMultiAnalysis();
	}, [closeMultiAnalysis]);

	const handleSelectCall = React.useCallback(
		(callId: string, _selected: boolean, isBulk?: boolean) => {
			handleToggleCall(callId, isBulk);
			if (isBulk) {
				setBulkSelectionKey(prev => prev + 1);
			}
		},
		[handleToggleCall]
	);

	const handleSelectAll = React.useCallback(
		(_selected: boolean) => {
			handleToggleSelectAll();
		},
		[handleToggleSelectAll]
	);

	const handleAnalyzeMultipleCalls = React.useCallback(
		(analysisTypeKey: string) => {
			const analysisType = ANALYSIS_TYPES.find(type => type.key === analysisTypeKey);
			if (!analysisType || selectedCalls.length === 0) return;

			startMultiAnalysis(analysisType, customSystemMessage.trim() || undefined);
		},
		[startMultiAnalysis, selectedCalls.length, customSystemMessage]
	);

	const handleClearFilters = React.useCallback(() => {
		setFilter(DefaultPracticeCallFilter);
	}, [setFilter]);

	const handleSort = React.useCallback((newSort: Api.ISortDescriptor) => {
		setSort(newSort);
	}, []);

	const handleSaveCustomMessage = React.useCallback(() => {
		// Message is saved when modal calls onMessageChange
	}, []);

	return (
		<div className={css(styleSheet.content)}>
			<PracticeCallFilters filter={filter} onFilterChange={setFilter} onClearFilters={handleClearFilters} />

			{/* Bulk analysis controls */}
			<div className={css(styleSheet.bulkActionsContainer)}>
				<div className={css(styleSheet.bulkActionsContent)}>
					<span className={css(styleSheet.selectedCount)}>
						{selectedCalls.length > 0
							? `${selectedCalls.length} call${selectedCalls.length !== 1 ? 's' : ''} selected`
							: 'No calls selected'}
					</span>
					<div className={css(styleSheet.bulkActions)}>
						{/* Custom System Message Inline Display */}
						<div>
							<div className={css(styleSheet.customMessageRow)}>
								<label className={css(styleSheet.customMessageLabel)}>
									Custom Message:
									{customSystemMessage.trim() ? (
										<span className={css(styleSheet.customMessagePreview)}>
											{customSystemMessage.length > 50
												? `"${customSystemMessage.substring(0, 50)}..."`
												: `"${customSystemMessage}"`}
										</span>
									) : (
										<span className={css(styleSheet.customMessagePlaceholder)}>None</span>
									)}
								</label>
								<Button onClick={() => setIsMessageModalOpen(true)} variant='secondary' size='sm'>
									{customSystemMessage.trim() ? 'Edit' : 'Add'}
								</Button>
							</div>
						</div>
						{ANALYSIS_TYPES.map(analysisType => (
							<AiButton
								key={analysisType.key}
								title={isLoadingMultiAnalysis ? 'Analyzing...' : 'Analyze'}
								onClick={() => handleAnalyzeMultipleCalls(analysisType.key)}
								disabled={isLoadingMultiAnalysis || selectedCalls.length === 0}
								isLoading={isLoadingMultiAnalysis}
								size='sm'
							/>
						))}
						<Button onClick={resetSelection} disabled={selectedCalls.length === 0} size='sm'>
							Clear Selection
						</Button>
					</div>
				</div>
			</div>

			<div className={css(styleSheet.tableContainer)}>
				<PracticeCallsTable
					calls={calls}
					isLoading={isLoadingCalls}
					sort={sort}
					onSort={handleSort}
					onClearFilters={handleClearFilters}
					loadMore={loadMore}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					selectedCallIds={selectedCallIds}
					onSelectCall={handleSelectCall}
					onSelectAll={handleSelectAll}
					isAllSelected={isAllSelected}
					isPartialSelected={isPartialSelected}
					isCallSelected={isCallSelected}
				/>
			</div>

			{/* Multi-call analysis modal */}
			<CallAnalysisModal
				isOpen={isMultiAnalysisModalOpen}
				onClose={handleCloseMultiAnalysis}
				analysisType={multiSelectedAnalysisType}
				aiResponse={multiAiResponse}
				isLoading={isLoadingMultiAnalysis}
				conversationMessages={multiConversationMessages}
				conversationId={multiConversationId}
				onSendFollowUp={sendMultiFollowUp}
				lastAiResponseCompleted={lastMultiAiResponseCompleted}
				isMultiCall={true}
				selectedCallCount={selectedCallCount}
			/>

			{/* Custom System Message Modal */}
			<CustomSystemMessageModal
				isOpen={isMessageModalOpen}
				onClose={() => setIsMessageModalOpen(false)}
				message={customSystemMessage}
				onMessageChange={setCustomSystemMessage}
				onSave={handleSaveCustomMessage}
			/>
		</div>
	);
};
